"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { getSafeDeepLinkUrl } from "@/lib/notifications/navigation";

const PUSH_DEEPLINK_EVENT = "push-notification-deeplink";
const PENDING_DEEPLINK_KEY = "growthsync_pending_push_deeplink";

export type PushDeeplinkDetail = { url: string };

/**
 * Navigates to a deep link URL. Defers slightly so router is ready (critical on cold start).
 */
function navigateToDeeplink(router: ReturnType<typeof useRouter>, safeUrl: string): void {
  // Defer navigation so Next.js router and app layout are ready (per Capacitor cold start findings)
  const attempt = () => {
    try {
      router.push(safeUrl);
    } catch (e) {
      console.warn("[Push Deeplink] router.push failed, retrying:", e);
      setTimeout(() => router.push(safeUrl), 100);
    }
  };
  // Small delay ensures app shell/layout have mounted when opening from cold start
  if (typeof requestAnimationFrame !== "undefined") {
    requestAnimationFrame(() => setTimeout(attempt, 150));
  } else {
    setTimeout(attempt, 150);
  }
}

/**
 * Listens for deep-link requests from push notification taps and navigates
 * using the Next.js router (client-side) so the app opens the exact screen
 * instead of the default page.
 */
export function PushNotificationDeepLinkHandler() {
  const router = useRouter();
  const hasProcessedPendingRef = useRef(false);

  useEffect(() => {
    const processDeeplink = (url: string) => {
      const safeUrl = getSafeDeepLinkUrl(url);
      try {
        sessionStorage.removeItem(PENDING_DEEPLINK_KEY);
      } catch {
        /* ignore */
      }
      navigateToDeeplink(router, safeUrl);
    };

    const handle = (e: Event) => {
      const detail = (e as CustomEvent<PushDeeplinkDetail>).detail;
      const url = detail?.url;
      if (url) processDeeplink(url);
    };

    window.addEventListener(PUSH_DEEPLINK_EVENT, handle);

    // Process pending deeplink if event fired before this handler mounted (cold start race)
    if (!hasProcessedPendingRef.current && typeof sessionStorage !== "undefined") {
      hasProcessedPendingRef.current = true;
      try {
        const pending = sessionStorage.getItem(PENDING_DEEPLINK_KEY);
        if (pending) {
          processDeeplink(pending);
        }
      } catch {
        /* ignore */
      }
    }

    return () => window.removeEventListener(PUSH_DEEPLINK_EVENT, handle);
  }, [router]);

  return null;
}

/**
 * Call from push notification tap handler to navigate to a deep link.
 * Dispatches an event so the app shell can use the router (avoids full reload).
 * Stores URL in sessionStorage as fallback if event fires before handler mounts.
 */
export function dispatchPushDeeplink(url: string | null | undefined): void {
  const safeUrl = getSafeDeepLinkUrl(url);
  // Store for cold start: handler may mount after event fires
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(PENDING_DEEPLINK_KEY, safeUrl);
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent(PUSH_DEEPLINK_EVENT, { detail: { url: safeUrl } })
  );
}
